import { ReactElement } from "react";

import styles from "./index.module.css";
import { useDrawings } from "../../../state/drawings";
import { useTool } from "../../../state/tool";
import { useKeyboardShortcuts } from "../../../lib/keyboard";
import { Drawing } from "../../../lib/drawing";
import { clear, flip, fill, redo, undo } from "../../../lib/drawing/paint";
import {
  reflectOverX,
  reflectOverY,
  rotateNeg,
  rotatePos,
  translateX,
  translateY,
} from "../../../lib/drawing/transform";
import IconButton from "../../shared/Icon/Button";

export default function Palette(props: {
  drawing: Drawing;
  horizontal?: boolean;
}): ReactElement {
  const { tool, setTool } = useTool();
  const { update } = useDrawings();
  const drawing = props.drawing;

  useKeyboardShortcuts({
    brush: () => setTool("brush"),
    flood: () => setTool("flood"),
    line: () => setTool("line"),
    undo: () => update(drawing, undo(drawing)),
    redo: () => update(drawing, redo(drawing)),
    translateLeft: () => update(drawing, translateX(drawing, -1)),
    translateRight: () => update(drawing, translateX(drawing, 1)),
    translateUp: () => update(drawing, translateY(drawing, 1)),
    translateDown: () => update(drawing, translateY(drawing, -1)),
    reflectX: () => update(drawing, reflectOverX(drawing)),
    reflectY: () => update(drawing, reflectOverY(drawing)),
    rotateNeg: () => update(drawing, rotateNeg(drawing)),
    rotatePos: () => update(drawing, rotatePos(drawing)),
    clear: () => update(drawing, clear(drawing)),
    fill: () => update(drawing, fill(drawing)),
    invert: () => update(drawing, flip(drawing)),
  });

  return (
    <>
      <div
        className={`${styles.layout} ${
          props.horizontal ? styles.horizontal : styles.vertical
        }`}
      >
        <section>
          <IconButton
            name={"brush"}
            highlighted={tool === "brush"}
            onClick={() => setTool("brush")}
            tooltip="brush tool"
          />

          <IconButton
            name={"flood"}
            highlighted={tool === "flood"}
            onClick={() => setTool("flood")}
            tooltip="flood tool"
          />

          <IconButton
            name={"line"}
            highlighted={tool === "line"}
            onClick={() => setTool("line")}
            tooltip="line tool"
          />
        </section>

        <section>
          <IconButton
            name={"undo"}
            disabled={!props.drawing.canUndo}
            onClick={() => update(drawing, undo(drawing))}
            tooltip="undo"
          />

          <IconButton
            name={"redo"}
            disabled={!props.drawing.canRedo}
            onClick={() => update(drawing, redo(drawing))}
            tooltip="redo"
          />
        </section>

        <section>
          <IconButton
            name={"left"}
            onClick={() => update(drawing, translateX(drawing, -1))}
            tooltip="move left"
          />

          <IconButton
            name={"right"}
            onClick={() => update(drawing, translateX(drawing, 1))}
            tooltip="move right"
          />

          <IconButton
            name={"up"}
            onClick={() => update(drawing, translateY(drawing, 1))}
            tooltip="move up"
          />

          <IconButton
            name={"down"}
            onClick={() => update(drawing, translateY(drawing, -1))}
            tooltip="move down"
          />
        </section>

        <section>
          <IconButton
            name={"reflect-y"}
            onClick={() => update(drawing, reflectOverY(drawing))}
            tooltip="reflect y"
          />

          <IconButton
            name={"reflect-x"}
            onClick={() => update(drawing, reflectOverX(drawing))}
            tooltip="reflect x"
          />
        </section>

        <section>
          <IconButton
            name={"rotate-neg"}
            onClick={() => update(drawing, rotateNeg(drawing))}
            tooltip="rotate -90°"
          />

          <IconButton
            name={"rotate-pos"}
            onClick={() => update(drawing, rotatePos(drawing))}
            tooltip="rotate 90°"
          />
        </section>

        <section>
          <IconButton
            name={"clear"}
            onClick={() => update(drawing, clear(drawing))}
            tooltip="clear"
          />
          <IconButton
            name={"fill"}
            onClick={() => update(drawing, fill(drawing))}
            tooltip="fill"
          />
          <IconButton
            name={"invert"}
            onClick={() => update(drawing, flip(drawing))}
            tooltip="invert"
          />
        </section>
      </div>
    </>
  );
}
